import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useUserPoller - React hook that polls a URL and returns the parsed JSON user object
 *
 * @param {Object} options
 * @param {string} options.url - endpoint to poll (default: http://localhost:8000)
 * @param {number} options.interval - poll interval in ms (default: 300000)
 * @param {boolean} options.autoStart - start polling immediately (default: true)
 *
 * @returns {{ user: any, loading: boolean, error: Error|null, start:Function, stop:Function }}
 */
export function useUserPoller({ url = process.env.REACT_APP_BASE_SERVER_URL + '/user.php', interval = 60000, autoStart = false  } = {}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const fetchOnce = useCallback(async (signal) => {
    
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(url, { signal, method: 'GET',  credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setUser(json);
      setLoading(false);
      console.log('Fetched user: ', json);
      return json;
    } catch (err) {
      console.log('Error: fetchonce ', err);
      if (err.name === 'AbortError') {
        // ignore abort
        return null;
      }
      setError(err);
      setLoading(false);
      return null;
    }
  }, [url]);

  const start = useCallback(() => {
    console.log('UserPoller: start polling');
    // avoid multiple timers
    
    if (timerRef.current) return;
     
    // initial fetch
    abortRef.current = new AbortController();
    fetchOnce(abortRef.current.signal);
    // subsequent polls
    timerRef.current = setInterval(() => {
      // abort previous pending fetch
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();
      fetchOnce(abortRef.current.signal);
    }, interval);
  }, [fetchOnce, interval]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (autoStart) start();
    return () => stop();
  }, [autoStart, start, stop]);

  return { user, loading, error, start, stop };
}

/**
 * UserPoller component - polls an endpoint and renders nothing by default.
 * Accepts a render prop `children` which will be called with ({user,loading,error,start,stop}).
 *
 * <UserPoller url="http://localhost:8000" interval={3000}>
 *   {({user}) => user ? <div>{user.name}</div> : <div>no user</div> }
 * </UserPoller>
 */
export function UserPoller({ url = process.env.REACT_APP_BASE_SERVER_URL + '/user.php', interval = 60000, autoStart = true, children = null }) {
  const { user, loading, error, start, stop } = useUserPoller({ url, interval, autoStart });

  if (typeof children === 'function') {
    return children({ user, loading, error, start, stop });
  }

  // default: render nothing (invisible background poller)
  return null;
}

export default UserPoller;
